import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { api, thumb } from '@/lib/api';
import { CouponBox } from '@/components/CouponBox';
import { trackInitiateCheckout, trackAddPaymentInfo } from '@/lib/analytics';
import { toast } from 'sonner';
import {
  ShoppingCart, Trash2, Minus, Plus, Loader2, Lock, ShieldCheck, ArrowLeft,
  ShoppingBag, Sparkles,
} from 'lucide-react';

export default function CartPage() {
  const { items, setQty, remove, subtotal, count, add } = useCart();
  const { user } = useAuth();
  const [checkingOut, setCheckingOut] = useState(false);
  const [email, setEmail] = useState('');
  const [upsells, setUpsells] = useState([]);
  const [coupon, setCoupon] = useState(null); // {code, discount, message}
  const nav = useNavigate();

  const discount = coupon ? Math.min(coupon.discount, subtotal) : 0;
  const total = Math.max(0, subtotal - discount);

  const inCartIds = useMemo(() => new Set(items.map(i => i.product_id)), [items]);

  // "Goes great with" — AI related products for what's already in the cart
  useEffect(() => {
    if (items.length === 0) { setUpsells([]); return; }
    let alive = true;
    const ids = [...new Set(items.map(i => i.product_id))].slice(0, 2);
    Promise.all(ids.map(id =>
      api.get(`/shop/products/${id}/related`, { params: { limit: 4 } })
        .then(r => r.data.products || []).catch(() => [])
    )).then(results => {
      if (!alive) return;
      const seen = new Set();
      const merged = [];
      for (const p of results.flat()) {
        if (inCartIds.has(p.id) || seen.has(p.id)) continue;
        seen.add(p.id);
        merged.push(p);
        if (merged.length >= 4) break;
      }
      setUpsells(merged);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const addUpsell = (p) => {
    if (p.has_variants) { nav(`/shop/${p.id}`); return; }
    add(p, 1);
  };

  const checkout = async () => {
    const guestEmail = email.trim().toLowerCase();
    if (!user && guestEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
      toast.error('That email doesn\u2019t look right — fix it or leave it blank');
      return;
    }
    setCheckingOut(true);
    const contentIds = items.map(i => i.sku || i.product_id);
    trackInitiateCheckout(subtotal, count, contentIds);
    trackAddPaymentInfo(subtotal, count, contentIds); // handing off to Stripe payment
    try {
      const body = {
        purpose: 'cart',
        items: items.map(i => ({ product_id: i.product_id, variant_vid: i.variant_vid || undefined, quantity: i.qty })),
        customer_email: user?.email || guestEmail || undefined,
        coupon: coupon?.code || undefined,
      };
      const r = await api.post('/stripe/checkout', body);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed — please try again');
      setCheckingOut(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="cart-page">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#17352D]">
          <ArrowLeft className="h-4 w-4" /> Keep shopping
        </Link>
        <h1 className="mt-3 font-headline text-3xl md:text-4xl text-[#17352D] flex items-center gap-3">
          <ShoppingCart className="h-7 w-7 text-[#C8922F]" /> Your Cart
          {count > 0 && <span className="text-base font-sans font-normal text-slate-500">({count} {count === 1 ? 'item' : 'items'})</span>}
        </h1>

        {items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-slate-200 p-14 text-center" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 mx-auto text-slate-300" />
            <div className="mt-4 font-headline text-2xl text-[#17352D]">Your cart is empty</div>
            <p className="mt-2 text-slate-600">Browse the shop and add products — they'll wait for you here.</p>
            <Button asChild className="mt-6 rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-11 px-6">
              <Link to="/shop" data-testid="cart-empty-shop-btn">Browse the Shop</Link>
            </Button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3">
              {items.map(i => (
                <div key={i.key} className="rounded-2xl border border-[#E6E9EE] bg-white p-3 sm:p-4 flex gap-3 sm:gap-4 items-center" data-testid={`cart-item-${i.product_id}`}>
                  <Link to={`/shop/${i.product_id}`} className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl overflow-hidden bg-[#F2F7F5] flex items-center justify-center shrink-0">
                    {i.image ? (
                      <img src={thumb(i.image, 200)} alt={i.name} loading="lazy" decoding="async" className="h-full w-full object-cover" onError={(e) => { if (i.image && e.currentTarget.src !== i.image) { e.currentTarget.src = i.image; return; } e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <ShoppingBag className="h-8 w-8 text-slate-300" />
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/shop/${i.product_id}`} className="font-semibold text-[#17352D] line-clamp-2 hover:underline text-sm sm:text-base">{i.name}</Link>
                    {i.variant_name && <div className="text-xs text-slate-500 mt-0.5 truncate">Option: {i.variant_name}</div>}
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                      <div className="inline-flex items-center border border-[#E6E9EE] rounded-xl overflow-hidden">
                        <button onClick={() => setQty(i.key, i.qty - 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#F2F7F5] disabled:opacity-40" disabled={i.qty <= 1} data-testid={`cart-qty-minus-${i.product_id}`} aria-label="Decrease quantity">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <div className="h-9 min-w-[2.5ch] px-2 flex items-center justify-center text-sm font-semibold text-[#17352D]" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</div>
                        <button onClick={() => setQty(i.key, i.qty + 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#F2F7F5]" data-testid={`cart-qty-plus-${i.product_id}`} aria-label="Increase quantity">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button onClick={() => remove(i.key)} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600" data-testid={`cart-remove-${i.product_id}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-headline text-lg text-[#17352D]">${(i.price * i.qty).toFixed(2)}</div>
                    {i.qty > 1 && <div className="text-[11px] text-slate-500">${i.price.toFixed(2)} each</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:sticky lg:top-24 self-start rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="cart-summary">
              <div className="font-headline text-xl text-[#17352D]">Order Summary</div>
              <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                <span>Subtotal ({count} {count === 1 ? 'item' : 'items'})</span>
                <span className="font-semibold text-[#17352D]" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                <span>Shipping</span>
                <span className="text-emerald-700 font-semibold">FREE</span>
              </div>
              <div className="mt-3">
                <CouponBox subtotal={subtotal} coupon={coupon} onChange={setCoupon} />
              </div>
              {discount > 0 && (
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-emerald-700">Discount ({coupon.code})</span>
                  <span className="font-semibold text-emerald-700" data-testid="cart-discount">-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[#E6E9EE] flex items-center justify-between">
                <span className="font-semibold text-[#17352D]">Total</span>
                <span className="font-headline text-2xl text-[#17352D]" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              {!user && (
                <div className="mt-4">
                  <Label className="text-xs">Email for order updates (optional)</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="rounded-xl mt-1 h-10"
                    data-testid="cart-email-input"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">We'll save your cart and send order updates.</div>
                </div>
              )}
              <Button onClick={checkout} disabled={checkingOut} className="mt-5 w-full rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-12" data-testid="cart-checkout-btn">
                {checkingOut ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</> : <><Lock className="h-4 w-4 mr-2" /> Secure Checkout</>}
              </Button>
              <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-[#C8922F]" /> Secure payment powered by Stripe · 7-day returns
              </div>
            </div>
          </div>
        )}

        {/* Goes great with — AI upsells */}
        {items.length > 0 && upsells.length > 0 && (
          <div className="mt-12" data-testid="cart-upsells">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-[#C8922F]" />
              <h2 className="font-headline text-2xl text-[#17352D]">Goes great with these</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {upsells.map(p => (
                <div key={p.id} className="rounded-2xl border border-[#E6E9EE] bg-white overflow-hidden group" data-testid={`cart-upsell-${p.id}`}>
                  <Link to={`/shop/${p.id}`} className="block">
                    <div className="aspect-square bg-[#F2F7F5] overflow-hidden">
                      {p.image && <img src={thumb(p.image, 400)} alt={p.name} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform-only" onError={(e) => { if (p.image && e.currentTarget.src !== p.image) e.currentTarget.src = p.image; }} />}
                    </div>
                  </Link>
                  <div className="p-3">
                    <Link to={`/shop/${p.id}`} className="text-sm font-semibold text-[#17352D] line-clamp-2 hover:underline">{p.name}</Link>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="font-headline text-lg text-[#17352D]">${Number(p.price).toFixed(2)}</span>
                      <button
                        onClick={() => addUpsell(p)}
                        data-testid={`cart-upsell-add-${p.id}`}
                        aria-label={`Add ${p.name} to cart`}
                        className="h-8 px-3 rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white text-xs font-semibold inline-flex items-center gap-1 transition-colors-only"
                      >
                        <Plus className="h-3.5 w-3.5" /> Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </Layout>
  );
}
